/**
 * AWENI — Main Entry Point
 *
 * Bootstraps the app: initializes context providers, mounts the shell,
 * registers the router, sets up service workers, runs onboarding,
 * and tracks performance metrics.
 */

import './styles/globals.css';
import './styles/themes.css';
import enJson from './i18n/en.json';
import { AuthProvider } from './context/AuthProvider';
import { ThemeProvider } from './context/ThemeProvider';
import { setContext } from './context/AweniContext';
import type {
  AweniContext,
  I18nState,
  NotificationState,
  OfflineState,
} from './context/AweniContext';
import { createRouter } from './router';
import { mountApp } from './App';
import { isOnboarded, mountOnboarding } from './shell/Onboarding';
import { NativeService } from './services/NativeService';

const API_BASE = '/identity';

// ---------- Performance Monitoring ----------

const perfMarks = {
  bootstrapStart: 'aweni:bootstrap-start',
  bootstrapEnd: 'aweni:bootstrap-end',
  shellRendered: 'aweni:shell-rendered',
  i18nLoaded: 'aweni:i18n-loaded',
  moduleLoaded: 'aweni:module-loaded',
};

// Numeric timings captured at mark-time. Used by logPerformanceSummary
// instead of performance.measure(), because the perf-entry buffer is
// bounded and idle-callback scheduling can defer logging by minutes —
// resolving marks lazily then yields nonsense readings (a real instance
// logged "bootstrap-duration: 3,069,931ms" after a 51-min idle tab).
const perfTimings: { bootstrapStart?: number; bootstrapEnd?: number; i18nLoaded?: number } = {};

function markPerf(name: string): void {
  try {
    performance.mark(name);
  } catch {
    /* Performance API not available */
  }
}

function measurePerf(name: string, startMark: string, endMark: string): void {
  try {
    performance.measure(name, startMark, endMark);
    const entries = performance.getEntriesByName(name);
    if (entries.length > 0) {
      const duration = entries[entries.length - 1].duration;
      // eslint-disable-next-line no-console -- intentional perf tracing
      console.log(`[AWENI perf] ${name}: ${duration.toFixed(1)}ms`);
    }
  } catch {
    /* Performance API not available */
  }
}

/** Log all key performance metrics after app is interactive. */
/* eslint-disable no-console -- this whole helper exists to print perf timings */
function logPerformanceSummary(): void {
  try {
    // Shell render time: DOMContentLoaded to first paint
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const nav = navEntries[0];
      console.log(`[AWENI perf] DOM Content Loaded: ${nav.domContentLoadedEventEnd.toFixed(1)}ms`);
      console.log(`[AWENI perf] DOM Complete: ${nav.domComplete.toFixed(1)}ms`);
    }

    // Paint timing
    const paintEntries = performance.getEntriesByType('paint');
    for (const entry of paintEntries) {
      console.log(`[AWENI perf] ${entry.name}: ${entry.startTime.toFixed(1)}ms`);
    }

    // Bootstrap + i18n durations — log from captured numbers (see perfTimings
    // comment). performance.measure() against perfMarks is intentionally
    // avoided here because this helper runs from a deferred idle callback.
    if (perfTimings.bootstrapStart != null && perfTimings.bootstrapEnd != null) {
      const ms = perfTimings.bootstrapEnd - perfTimings.bootstrapStart;
      console.log(`[AWENI perf] aweni:bootstrap-duration: ${ms.toFixed(1)}ms`);
    }
    if (perfTimings.bootstrapStart != null && perfTimings.i18nLoaded != null) {
      const ms = perfTimings.i18nLoaded - perfTimings.bootstrapStart;
      console.log(`[AWENI perf] aweni:i18n-duration: ${ms.toFixed(1)}ms`);
    }
  } catch {
    /* silent */
  }
}
/* eslint-enable no-console */

// ---------- i18n ----------

/** Simple i18n stub — loads JSON translations from /src/i18n/ */
async function createI18n(): Promise<I18nState> {
  const supportedLocales = ['en', 'fr', 'ar', 'es', 'de'];
  const staticLocale = 'en';
  let locale = localStorage.getItem('aweni_locale') || navigator.language.slice(0, 2);
  if (!supportedLocales.includes(locale)) locale = 'en';

  const translations: Record<string, Record<string, string>> = {
    [staticLocale]: enJson as Record<string, string>,
  };
  let current: Record<string, string> = translations[staticLocale] || {};

  const dir = (): 'ltr' | 'rtl' => (locale === 'ar' ? 'rtl' : 'ltr');

  const applyDir = () => {
    document.documentElement.setAttribute('dir', dir());
    document.documentElement.setAttribute('lang', locale);
  };

  const loadLocale = async (loc: string) => {
    if (loc === staticLocale) {
      current = translations[staticLocale] || {};
      markPerf(perfMarks.i18nLoaded);
      perfTimings.i18nLoaded ??= performance.now();
      return;
    }
    if (!translations[loc]) {
      try {
        const mod = await import(`./i18n/${loc}.json`);
        translations[loc] = mod.default || mod;
      } catch {
        translations[loc] = {};
      }
    }
    current = translations[loc] || {};
    markPerf(perfMarks.i18nLoaded);
    perfTimings.i18nLoaded ??= performance.now();
  };

  // T1-06 — static default locale boot perf via localStorage cache:
  //
  // On the very first session for a non-EN user, the shell does what it
  // always did: await the dynamic import of the locale JSON before mounting
  // (so we don't flash EN keys). On EVERY subsequent boot, the locale JSON
  // we already loaded is available from localStorage SYNCHRONOUSLY — skip
  // the network round-trip entirely.
  //
  // Net effect: −80–120ms First Paint on returning fr/ar/es/de users (the
  // ~99% case after first session). Cache key: `aweni_i18n_${locale}`.
  // Eviction: the cache is overwritten by every fresh fetch, so a deploy
  // that changes a key auto-refreshes within one session of staleness.
  const cacheKey = `aweni_i18n_${locale}`;
  let usedCache = false;
  if (locale !== staticLocale) {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, string>;
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 10) {
          translations[locale] = parsed;
          current = parsed;
          markPerf(perfMarks.i18nLoaded);
          perfTimings.i18nLoaded ??= performance.now();
          usedCache = true;
          // Revalidate in background — pick up any new keys silently.
          void loadLocale(locale).then(() => {
            const fresh = translations[locale];
            if (fresh) {
              try { localStorage.setItem(cacheKey, JSON.stringify(fresh)); } catch { /* */ }
            }
          });
        }
      }
    } catch { /* corrupt cache → fall through to await-fetch */ }
  }
  if (!usedCache) {
    await loadLocale(locale);
    if (locale !== staticLocale && translations[locale]) {
      try { localStorage.setItem(cacheKey, JSON.stringify(translations[locale])); } catch { /* */ }
    }
  }
  applyDir();

  const state: I18nState = {
    locale,
    dir: dir(),
    supportedLocales,
    t(key: string, params?: Record<string, string | number>): string {
      let s = current[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          s = s.replaceAll(`{${k}}`, String(v));
        }
      }
      return s;
    },
    setLocale(newLocale: string) {
      if (!supportedLocales.includes(newLocale)) return;
      locale = newLocale;
      localStorage.setItem('aweni_locale', locale);
      loadLocale(locale).then(() => {
        state.locale = locale;
        state.dir = dir();
        applyDir();
      });
    },
  };
  return state;
}

// ---------- Notifications ----------

/** Notification state stub — fetches from platform API */
function createNotifications(getToken: () => string | null): NotificationState {
  const state: NotificationState = {
    items: [],
    unreadCount: 0,

    async fetch() {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch('/identity/notifications', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          state.items = data.notifications || [];
          state.unreadCount = state.items.filter((n) => !n.read).length;
        }
      } catch {
        // Offline or server error — keep existing items
      }
    },

    async markAllRead() {
      const token = getToken();
      if (!token) return;
      try {
        await fetch('/identity/notifications/read', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        state.items.forEach((n) => (n.read = true));
        state.unreadCount = 0;
      } catch {
        /* silent */
      }
    },

    async markRead(id: string) {
      const token = getToken();
      if (!token) return;
      try {
        await fetch('/identity/notifications/read', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: [id] }),
          credentials: 'include',
        });
        const item = state.items.find((n) => n.id === id);
        if (item) {
          item.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      } catch {
        /* silent */
      }
    },
  };
  return state;
}

// ---------- Offline ----------

/** Offline connectivity state */
function createOfflineState(): OfflineState {
  const cbs: Array<(online: boolean) => void> = [];
  const state: OfflineState = {
    isOnline: navigator.onLine,
    onStatusChange(cb) {
      cbs.push(cb);
    },
  };

  window.addEventListener('online', () => {
    state.isOnline = true;
    cbs.forEach((cb) => cb(true));
  });
  window.addEventListener('offline', () => {
    state.isOnline = false;
    cbs.forEach((cb) => cb(false));
  });

  return state;
}

// ---------- Activity Tracking ----------

/** Activity tracking — fire-and-forget POST */
function trackActivity(token: string | null, action: string, data?: Record<string, unknown>) {
  if (!token) return;
  fetch('/api/activity/track', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ app: 'aweni', action, ...data }),
    credentials: 'include',
  }).catch(() => {
    /* fire-and-forget */
  });
}

// ---------- Accessibility: aria-live route announcer ----------

function createRouteAnnouncer(): HTMLElement {
  const announcer = document.createElement('div');
  announcer.id = 'aweni-route-announcer';
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.style.cssText =
    'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
  document.body.appendChild(announcer);
  return announcer;
}

// ---------- Bootstrap ----------

/**
 * Eradicate any existing Service Worker + all caches before bootstrap.
 *
 * Firefox + ETP/RFP was observed to cancel in-flight chunk + icon fetches
 * with NS_BINDING_ABORTED whenever a SW was the page controller, even with
 * minimal precache, modulePreload disabled, deferred imports, and a clean
 * SW lifecycle. Edge/Chrome on the same deploy work flawlessly. Until the
 * Firefox-specific cause is isolated, we run AWENI as a regular SPA: no
 * service worker, no precaching, no PWA install. The cost is offline
 * support; the gain is a functioning app for every browser.
 *
 * Returns true if a reload was triggered (caller should bail and let
 * the freshly-loaded page run uncontrolled).
 */
async function eradicateServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (regs.length === 0) return false;
    // Unregister every SW within /aweni/ scope (and any wildcard regs).
    let didUnregister = false;
    for (const reg of regs) {
      if (reg.scope.includes('/aweni')) {
        await reg.unregister();
        didUnregister = true;
      }
    }
    // Clear all caches the SW may have populated.
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if (didUnregister) {
      // Reload uncontrolled. Subsequent loads have no SW intercepting.
      window.location.reload();
      return true;
    }
  } catch {
    /* best-effort — continue regardless */
  }
  return false;
}

/**
 * Global error handlers — log uncaught exceptions + promise rejections
 * with enough diagnostic info to debug Firefox-specific edge cases. If
 * any module throws or a Promise rejects unhandled, this logs the full
 * error with controller state, online status, and URL — visible in the
 * user's console when they paste a log.
 */
function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (e) => {
    const diag = {
      type: 'window.error',
      message: e.message,
      filename: e.filename,
      line: e.lineno,
      col: e.colno,
      stack: e.error?.stack?.split('\n').slice(0, 5).join(' | '),
      url: window.location.href,
      hash: window.location.hash,
      controller: navigator.serviceWorker?.controller?.scriptURL || 'none',
      online: navigator.onLine,
    };
    console.error('[AWENI uncaught]', JSON.stringify(diag));
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason: { name?: string; message?: string; stack?: string } | null =
      e.reason instanceof Error
        ? {
            name: e.reason.name,
            message: e.reason.message,
            stack: e.reason.stack?.split('\n').slice(0, 5).join(' | '),
          }
        : { message: String(e.reason) };
    const diag = {
      type: 'unhandledrejection',
      reason,
      url: window.location.href,
      hash: window.location.hash,
      controller: navigator.serviceWorker?.controller?.scriptURL || 'none',
      online: navigator.onLine,
    };
    console.error('[AWENI unhandled rejection]', JSON.stringify(diag));
  });
  // Beforeunload = visibility into "page is navigating away". Catches the
  // case where something redirects the user out of /aweni/ without us knowing.
  window.addEventListener('beforeunload', () => {
    console.warn('[AWENI nav] page is unloading — final URL:', window.location.href);
  });
}

/** Bootstrap the AWENI application */
async function bootstrap() {
  // Install error handlers FIRST, before anything that could throw.
  installGlobalErrorHandlers();

  markPerf(perfMarks.bootstrapStart);
  perfTimings.bootstrapStart = performance.now();

  // Eradicate any leftover SW first. If reload happens, the next load
  // runs uncontrolled — no SW intercepts page chunks, icons, or fetches.
  if (await eradicateServiceWorker()) return;

  const app = document.getElementById('app');
  if (!app) throw new Error('Missing #app element');

  // Initialize providers
  const auth = new AuthProvider();
  const theme = new ThemeProvider();
  const i18n = await createI18n();
  const notifications = createNotifications(() => auth.getToken());
  const offline = createOfflineState();

  // Create router
  const router = createRouter();

  // Create route announcer for screen readers
  const routeAnnouncer = createRouteAnnouncer();

  // Build context
  const ctx: AweniContext = {
    auth,
    i18n,
    theme,
    notifications,
    offline,
    navigate: (path: string) => router.navigate(path),
    trackActivity: (action: string, data?: Record<string, unknown>) =>
      trackActivity(auth.getToken(), action, data),
    apiBase: API_BASE,
  };

  setContext(ctx);

  // ---------- Native platform initialization ----------
  if (NativeService.isNative()) {
    // Set status bar to match dark theme
    NativeService.setStatusBarStyle(theme.isDark ? 'dark' : 'light');
    NativeService.setStatusBarColor('#0F172A');

    // Sync status bar on theme changes
    theme.onChange((isDark) => {
      NativeService.setStatusBarStyle(isDark ? 'dark' : 'light');
      NativeService.setStatusBarColor(isDark ? '#0F172A' : '#FFFFFF');
    });
  }

  // Android back button: navigate back or minimize app
  NativeService.onBackButton(() => {
    if (window.history.length > 1) {
      window.history.back();
    }
    // If at root, Android default behavior (minimize) applies
  });

  // Pause/resume: refresh notifications on resume
  NativeService.onAppStateChange((isActive) => {
    if (isActive && auth.isAuthenticated) {
      notifications.fetch();
    }
  });

  // Show onboarding if first time
  if (!isOnboarded()) {
    await mountOnboarding(app, ctx);
  }

  // Mount the app shell. `?shell=minimal` strips TopBar/BottomNav/Footer
  // for standalone serving of /hisni/ and /yawmi/ URLs (per ST3 spec +
  // docs/MODULE-INTEGRATION.md). The bundle is the same; only chrome differs.
  const shellMode = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('shell') === 'minimal' ? 'minimal' : 'full';
    } catch { return 'full'; }
  })();
  mountApp(app, ctx, router, { shellMode });

  markPerf(perfMarks.shellRendered);

  // Announce route changes to screen readers + measure each transition's
  // own duration (previous implementation always measured from shellRendered,
  // so a navigation after 2 minutes of idle time logged "module-switch: 152s"
  // — useless noise in the console). We now measure between consecutive
  // module-load marks: the first transition reports vs. shellRendered,
  // every subsequent one reports vs. the prior moduleLoaded mark.
  let _lastSwitchMark = perfMarks.shellRendered;
  let _switchSeq = 0;
  router.onChange((route: { module: string }) => {
    const moduleKey = `nav_${route.module}`;
    const translated = i18n.t(moduleKey);
    const label = translated !== moduleKey ? translated : route.module;
    routeAnnouncer.textContent = label;

    const thisMark = `${perfMarks.moduleLoaded}-${_switchSeq++}`;
    markPerf(thisMark);
    measurePerf(`aweni:module-switch`, _lastSwitchMark, thisMark);
    _lastSwitchMark = thisMark;
  });

  // Load notifications in the background
  if (auth.isAuthenticated) {
    notifications.fetch();
  }

  // Service Worker registration is owned by VitePWA's auto-injected
  // /aweni/registerSW.js (see dist/index.html). DO NOT register here too —
  // double registration races the lifecycle and was a contributor to the
  // recurring NS_BINDING_ABORTED on dynamic imports. UpdateManager drives
  // SKIP_WAITING via postMessage when the user explicitly accepts an update.
  // See AWENI/CLAUDE.md "Module Load Invariants".

  // PWA install prompt — managed by InstallManager. Loaded LATE and
  // fire-and-forget: must NEVER block bootstrap or compete with the
  // active module's import. Previously this `await import()` was
  // observed to race with HomeModule + scheduleIdlePreload's GamesAdapter
  // import on Firefox + ETP, causing all three to abort with
  // NS_BINDING_ABORTED. Defer to after first paint via requestIdleCallback.
  const loadInstallManager = () => {
    import('./services/InstallManager')
      .then(({ getInstallManager }) => {
        const installMgr = getInstallManager();
        installMgr.onInstalled(() => installMgr.showCelebration());
      })
      .catch(() => {
        /* non-critical — install banner is optional UX */
      });
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(loadInstallManager, { timeout: 3000 });
  } else {
    setTimeout(loadInstallManager, 1500);
  }

  // Install banner — DISABLED until app stores are live (Google Play + iOS App Store).
  // See memory: project_cta_reactivation.md. When reactivating, do it inside
  // the deferred loadInstallManager() above to keep install UX off the
  // bootstrap critical path.

  markPerf(perfMarks.bootstrapEnd);
  perfTimings.bootstrapEnd = performance.now();

  // Log performance summary after everything is ready. Cap the idle wait
  // at 3s so a backgrounded tab can't defer the log indefinitely (and so
  // it can't run after the perf-entry buffer has been evicted).
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => logPerformanceSummary(), { timeout: 3000 });
  } else {
    setTimeout(() => logPerformanceSummary(), 1000);
  }
}

bootstrap();
